import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ImageUploader, type ImageFieldValue } from "../forms/ImageUploader";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  CreateProjectPayload,
  Project,
  ProjectInvestmentOption,
  UpdateProjectPayload,
} from "../../types/api";
import { resolveAssetUrl } from "../../utils/url";

type ProjectFormModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    payload: CreateProjectPayload | UpdateProjectPayload,
    isEdit: boolean
  ) => Promise<void>;
  isSubmitting: boolean;
  project?: Project | null;
};

const defaultForm: CreateProjectPayload = {
  name: "",
  symbol: "",
  category: "",
  description: "",
  status: "open",
  goal: 10_000,
  activeContributors: 0,
};

const defaultImageValue: ImageFieldValue = { mode: "none", previewUrl: null };

type UploadedGraphImage = {
  base64: string;
  previewUrl: string;
};

type ExistingGraphImage = {
  id: number;
  imageUrl: string;
  previewUrl: string;
};

const ProjectFormModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  project,
}: ProjectFormModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<
    CreateProjectPayload & { openingDate: string }
  >({
    ...defaultForm,
    openingDate: "",
  });
  const [imageValue, setImageValue] =
    useState<ImageFieldValue>(defaultImageValue);
  const [graphImageUrls, setGraphImageUrls] = useState<string[]>([""]);
  const [uploadedGraphImages, setUploadedGraphImages] = useState<
    UploadedGraphImage[]
  >([]);
  const [existingGraphImages, setExistingGraphImages] = useState<
    ExistingGraphImage[]
  >([]);
  const [options, setOptions] = useState<
    Array<{ amount: string; returnPercentage: string; durationDays: string }>
  >([{ amount: "1000", returnPercentage: "10", durationDays: "180" }]);
  const [expandedOptions, setExpandedOptions] = useState<Set<number>>(
    new Set([0])
  );

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name,
        symbol: project.symbol,
        category: project.category,
        description: project.description,
        status: project.status,
        goal: project.goal,
        activeContributors: project.activeContributors || 0,
        openingDate: project.openingDate
          ? new Date(project.openingDate).toISOString().slice(0, 16)
          : "",
      });
      if (project.investmentOptions && project.investmentOptions.length > 0) {
        const opts = project.investmentOptions.map((opt) => ({
          amount: String(opt.amount),
          returnPercentage: String(opt.returnPercentage),
          durationDays: String(opt.durationDays),
        }));
        setOptions(opts);
        // Toutes les options sont fermées par défaut lors de l'édition
        setExpandedOptions(new Set());
      } else {
        setOptions([
          { amount: "1000", returnPercentage: "10", durationDays: "180" },
        ]);
        setExpandedOptions(new Set([0]));
      }
      if (project.imageUrl) {
        const isExternal =
          project.imageUrl.startsWith("http://") ||
          project.imageUrl.startsWith("https://");
        setImageValue({
          mode: "existing",
          previewUrl: resolveAssetUrl(project.imageUrl),
          externalUrl: isExternal ? project.imageUrl : undefined,
        });
      } else {
        setImageValue(defaultImageValue);
      }
      // Pré-remplir les images supplémentaires si présentes
      if (project.images && project.images.length > 0) {
        // Séparer les images existantes (pour affichage) et les nouvelles URLs à ajouter
        const existingImages: ExistingGraphImage[] = project.images
          .filter((img) => img.imageUrl)
          .map((img) => ({
            id: img.id,
            imageUrl: img.imageUrl!,
            previewUrl: resolveAssetUrl(img.imageUrl!) ?? img.imageUrl!,
          }));
        setExistingGraphImages(existingImages);
        setGraphImageUrls([""]);
        setUploadedGraphImages([]);
      } else {
        setExistingGraphImages([]);
        setGraphImageUrls([""]);
        setUploadedGraphImages([]);
      }
    } else {
      setForm({ ...defaultForm, openingDate: "" });
      setImageValue(defaultImageValue);
      setOptions([
        { amount: "1000", returnPercentage: "10", durationDays: "30" },
      ]);
      setGraphImageUrls([""]);
      setUploadedGraphImages([]);
      setExistingGraphImages([]);
      setExpandedOptions(new Set([0]));
    }
  }, [project]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // S'assurer que goal est un nombre
    const goal =
      typeof form.goal === "string"
        ? Number.parseFloat(form.goal) || defaultForm.goal
        : form.goal;

    const validOptions = options
      .map((opt) => ({
        amount: Number.parseFloat(opt.amount),
        returnPercentage: Number.parseFloat(opt.returnPercentage),
        durationDays: Number.parseInt(opt.durationDays, 10),
      }))
      .filter(
        (opt) =>
          Number.isFinite(opt.amount) &&
          opt.amount > 0 &&
          Number.isFinite(opt.returnPercentage) &&
          opt.returnPercentage > 0 &&
          Number.isInteger(opt.durationDays) &&
          opt.durationDays > 0
      );

    const payload: CreateProjectPayload | UpdateProjectPayload = {
      ...form,
      goal,
      investmentOptions: validOptions.length > 0 ? validOptions : undefined,
    };

    // Pour les images de la galerie :
    // - Si on est en mode édition ET qu'il n'y a pas de nouvelles images/URLs ET qu'on n'a pas supprimé d'images existantes, on ne touche pas aux images
    // - Sinon, on envoie la liste finale (images existantes restantes + nouvelles)
    const newUrls = graphImageUrls
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
    const hasNewImages = newUrls.length > 0 || uploadedGraphImages.length > 0;
    const hasRemovedExisting =
      project && existingGraphImages.length < (project.images?.length ?? 0);

    if (hasNewImages || hasRemovedExisting || !project) {
      // Construire la liste finale : images existantes restantes (converties en URLs) + nouvelles URLs + nouvelles images base64
      const finalUrls: string[] = [];

      // Ajouter les URLs des images existantes qui n'ont pas été supprimées
      existingGraphImages.forEach((img) => {
        finalUrls.push(img.imageUrl);
      });

      // Ajouter les nouvelles URLs
      finalUrls.push(...newUrls);

      payload.graphImageUrls = finalUrls.length > 0 ? finalUrls : undefined;
      payload.graphImagesBase64 =
        uploadedGraphImages.length > 0
          ? uploadedGraphImages.map((img) => img.base64)
          : undefined;
    }
    // Si on est en édition et qu'on n'a rien modifié, on ne met pas graphImageUrls/graphImagesBase64
    // Le backend conservera les images existantes
    let removeImage = false;

    if (imageValue.mode === "upload" || imageValue.mode === "base64") {
      payload.imageBase64 = imageValue.base64 ?? undefined;
      payload.imageUrl = undefined;
    } else if (imageValue.mode === "external") {
      payload.imageUrl = imageValue.externalUrl ?? undefined;
      payload.imageBase64 = undefined;
    } else {
      payload.imageBase64 = undefined;
      payload.imageUrl = undefined;
      if (project?.imageUrl && imageValue.mode === "none") {
        removeImage = true;
      }
    }

    if (project && removeImage) {
      (payload as UpdateProjectPayload).removeImage = true;
    }

    // Ajouter openingDate si le statut est "upcoming" et qu'une date est définie
    if (form.status === "upcoming" && form.openingDate) {
      // Convertir la date locale en ISO string pour le backend
      const date = new Date(form.openingDate);
      payload.openingDate = date.toISOString();
    } else {
      payload.openingDate = null;
    }

    try {
      await onSubmit(payload, Boolean(project));
      onClose();
    } catch {
      // Les erreurs sont gérées par le parent
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-4xl overflow-auto bg-slate-950/95 text-white p-0">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-3xl font-bold text-white">
              {t("admin.dashboardTitle")}
            </DialogTitle>
            <p className="text-sm text-white/70 mt-2">
              {t("admin.dashboardSubtitle")}
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm p-6 shadow-lg">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.dashboardNameLabel")}</Label>
                  <Input
                    value={form.name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.dashboardSymbolLabel")}</Label>
                  <Input
                    value={form.symbol}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        symbol: event.target.value.toUpperCase(),
                      }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.dashboardCategoryLabel")}</Label>
                  <Input
                    value={form.category}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        category: event.target.value,
                      }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.dashboardStatusLabel")}</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        status: value as CreateProjectPayload["status"],
                        // Réinitialiser openingDate si on change de statut
                        openingDate:
                          value === "upcoming" ? prev.openingDate : "",
                      }))
                    }>
                    <SelectTrigger className="bg-white/5 text-white border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">
                        {t("admin.dashboardStatusOpen")}
                      </SelectItem>
                      <SelectItem value="closed">
                        {t("admin.dashboardStatusClosed")}
                      </SelectItem>
                      <SelectItem value="upcoming">
                        {t("admin.dashboardStatusUpcoming")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status === "upcoming" && (
                  <div className="space-y-2">
                    <Label>{t("admin.dashboardOpeningDateLabel")}</Label>
                    <Input
                      type="datetime-local"
                      value={form.openingDate}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          openingDate: event.target.value,
                        }))
                      }
                      className="bg-white/5 text-white border-white/10"
                    />
                    <p className="text-xs text-white/60">
                      {t("admin.dashboardOpeningDateHint")}
                    </p>
                  </div>
                )}
                <div className="space-y-2 md:col-span-2">
                  <Label>{t("admin.dashboardDescriptionLabel")}</Label>
                  <Textarea
                    value={form.description}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                    placeholder={t("admin.dashboardDescriptionPlaceholder")}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <ImageUploader
              value={imageValue}
              onChange={setImageValue}
              existingPreviewUrl={
                project?.imageUrl
                  ? resolveAssetUrl(project.imageUrl)
                  : undefined
              }
              existingExternalUrl={
                project?.imageUrl &&
                (project.imageUrl.startsWith("http://") ||
                  project.imageUrl.startsWith("https://"))
                  ? project.imageUrl
                  : undefined
              }
            />

            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-800/40 p-6 shadow-lg">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-base font-semibold text-white">
                    {t("admin.dashboardExtraImagesTitle")}
                  </h3>
                  <p className="text-xs text-white/60">
                    {t("admin.dashboardExtraImagesDescription")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white/80 hover:bg-white/5"
                    onClick={() => setGraphImageUrls((prev) => [...prev, ""])}>
                    + URL
                  </Button>
                  <label className="inline-flex cursor-pointer items-center rounded-md border border-emerald-400/60 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20">
                    {t("admin.dashboardImport")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          const result = reader.result;
                          if (typeof result === "string") {
                            setUploadedGraphImages((prev) => [
                              ...prev,
                              {
                                base64: result,
                                previewUrl: result,
                              },
                            ]);
                          }
                        };
                        reader.readAsDataURL(file);
                        // Réinitialiser le champ pour pouvoir ré-uploader le même fichier
                        event.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                {/* Afficher les images existantes */}
                {existingGraphImages.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-xs font-semibold text-white/70">
                      {t("admin.dashboardExistingImages")}
                    </p>
                    <div className="grid gap-3 md:grid-cols-3">
                      {existingGraphImages.map((img) => (
                        <div
                          key={img.id}
                          className="space-y-2 rounded-xl border border-cyan-400/40 bg-black/40 p-2">
                          <div className="aspect-video overflow-hidden rounded-lg bg-black/60">
                            <img
                              src={img.previewUrl}
                              alt={t("admin.dashboardExistingImageAlt", {
                                index: img.id,
                              })}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-red-300 hover:bg-red-500/10"
                            onClick={() =>
                              setExistingGraphImages((prev) =>
                                prev.filter((i) => i.id !== img.id)
                              )
                            }>
                            {t("admin.imageUploader.removeImage")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {graphImageUrls.map((url, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-2 rounded-xl border border-white/10 bg-black/30 p-3 md:flex-row md:items-center">
                    <div className="flex-1">
                      <Label className="text-xs text-white/70">
                        {t("admin.dashboardImageUrlLabel", { index: index + 1 })}
                      </Label>
                      <Input
                        type="url"
                        placeholder={t("admin.dashboardImageUrlPlaceholder")}
                        value={url}
                        onChange={(event) =>
                          setGraphImageUrls((prev) =>
                            prev.map((item, i) =>
                              i === index ? event.target.value : item
                            )
                          )
                        }
                        className="mt-1 bg-white/5 text-white border-white/10"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="self-start text-xs text-red-300 hover:bg-red-500/10"
                      onClick={() =>
                        setGraphImageUrls((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      disabled={graphImageUrls.length <= 1}>
                      {t("admin.imageUploader.removeImage")}
                    </Button>
                  </div>
                ))}

                {uploadedGraphImages.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold text-white/70">
                      {t("admin.dashboardUploadedImages")}
                    </p>
                    <div className="grid gap-3 md:grid-cols-3">
                      {uploadedGraphImages.map((img, index) => (
                        <div
                          key={index}
                          className="space-y-2 rounded-xl border border-emerald-400/40 bg-black/40 p-2">
                          <div className="aspect-video overflow-hidden rounded-lg bg-black/60">
                            <img
                              src={img.previewUrl}
                              alt={t("admin.dashboardUploadedImageAlt", {
                                index: index + 1,
                              })}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-red-300 hover:bg-red-500/10"
                            onClick={() =>
                              setUploadedGraphImages((prev) =>
                                prev.filter((_, i) => i !== index)
                              )
                            }>
                            {t("admin.imageUploader.removeImage")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm p-6 shadow-lg">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.dashboardGoalLabel")}</Label>
                  <Input
                    type="number"
                    value={form.goal}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        goal: Number(event.target.value) || defaultForm.goal,
                      }))
                    }
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.dashboardActiveContributorsLabel")}</Label>
                  <Input
                    type="number"
                    value={form.activeContributors ?? 0}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        activeContributors: Number(event.target.value) || 0,
                      }))
                    }
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/5 p-6 shadow-lg">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-300">
                    {t("admin.dashboardInvestmentOptionsTitle")}
                  </h3>
                  <p className="text-xs text-white/60">
                    {t("admin.dashboardInvestmentOptionsDescription")}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/10"
                  onClick={() => {
                    const newIndex = options.length;
                    setOptions((prev) => [
                      ...prev,
                      { amount: "", returnPercentage: "", durationDays: "" },
                    ]);
                    // Réduire toutes les autres options et agrandir la nouvelle
                    setExpandedOptions(new Set([newIndex]));
                  }}>
                  {t("admin.dashboardAddAmount")}
                </Button>
              </div>

              <div className="space-y-3">
                {options.map((opt, index) => {
                  const amount = Number.parseFloat(opt.amount || "0");
                  const percentage = Number.parseFloat(
                    opt.returnPercentage || "0"
                  );
                  const expectedGain =
                    Number.isFinite(amount) && Number.isFinite(percentage)
                      ? (amount * percentage) / 100
                      : 0;
                  const isExpanded = expandedOptions.has(index);
                  const isFilled =
                    opt.amount && opt.returnPercentage && opt.durationDays;

                  return (
                    <div
                      key={index}
                      className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
                      {/* En-tête cliquable pour toggle */}
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedOptions((prev) => {
                            const next = new Set(prev);
                            if (next.has(index)) {
                              next.delete(index);
                            } else {
                              next.add(index);
                            }
                            return next;
                          });
                        }}
                        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
                            {index + 1}
                          </div>
                          {isFilled ? (
                            <div className="flex-1 text-left">
                              <p className="text-sm font-semibold text-white">
                                {Number.parseFloat(
                                  opt.amount || "0"
                                ).toLocaleString()}{" "}
                                $ • {opt.returnPercentage}% • {opt.durationDays}{" "}
                                j
                              </p>
                              <p className="text-xs text-emerald-300">
                                {t("admin.dashboardEstimatedGain")}:{" "}
                                {expectedGain > 0
                                  ? `${expectedGain.toLocaleString()} $`
                                  : "-"}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-white/60">
                              {t("admin.dashboardOptionEmpty", {
                                index: index + 1,
                              })}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isFilled && (
                            <span className="text-xs text-emerald-300 font-medium">
                              {isExpanded
                                ? t("admin.dashboardCollapse")
                                : t("admin.dashboardExpand")}
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-white/60" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-white/60" />
                          )}
                        </div>
                      </button>

                      {/* Contenu expandable */}
                      {isExpanded && (
                        <div className="px-4 pb-4 grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))] md:items-end border-t border-white/10 pt-4">
                          <div className="space-y-1">
                            <Label className="text-xs text-white/70">
                              {t("admin.dashboardAmountLabel")}
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              value={opt.amount}
                              onChange={(event) =>
                                setOptions((prev) =>
                                  prev.map((item, i) =>
                                    i === index
                                      ? { ...item, amount: event.target.value }
                                      : item
                                  )
                                )
                              }
                              className="bg-white/5 text-white border-white/10"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-white/70">
                              {t("admin.dashboardReturnLabel")}
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.1}
                              value={opt.returnPercentage}
                              onChange={(event) =>
                                setOptions((prev) =>
                                  prev.map((item, i) =>
                                    i === index
                                      ? {
                                          ...item,
                                          returnPercentage: event.target.value,
                                        }
                                      : item
                                  )
                                )
                              }
                              className="bg-white/5 text-white border-white/10"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-white/70">
                              {t("admin.dashboardDurationDaysLabel")}
                            </Label>
                            <Input
                              type="number"
                              min={1}
                              step={1}
                              value={opt.durationDays}
                              onChange={(event) =>
                                setOptions((prev) =>
                                  prev.map((item, i) =>
                                    i === index
                                      ? {
                                          ...item,
                                          durationDays: event.target.value,
                                        }
                                      : item
                                  )
                                )
                              }
                              className="bg-white/5 text-white border-white/10"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <div>
                              <p className="text-xs text-white/60">
                                {t("admin.dashboardEstimatedGain")}
                              </p>
                              <p className="text-sm font-semibold text-emerald-300">
                                {expectedGain > 0
                                  ? `${expectedGain.toLocaleString()} $`
                                  : "-"}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="self-start text-xs text-red-300 hover:bg-red-500/10"
                              onClick={() => {
                                setOptions((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                                setExpandedOptions((prev) => {
                                  const next = new Set(prev);
                                  next.delete(index);
                                  // Réindexer les options restantes
                                  const reindexed = new Set<number>();
                                  next.forEach((idx) => {
                                    if (idx > index) {
                                      reindexed.add(idx - 1);
                                    } else {
                                      reindexed.add(idx);
                                    }
                                  });
                                  return reindexed;
                                });
                              }}
                              disabled={options.length <= 1}>
                              {t("admin.imageUploader.removeImage")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                className="text-white"
                onClick={onClose}
                disabled={isSubmitting}>
                {t("admin.cancel")}
              </Button>
              <Button
                type="submit"
                className="min-w-[180px] rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 text-base font-semibold shadow-lg shadow-cyan-500/30 transition hover:translate-y-[1px] hover:shadow-cyan-500/40"
                disabled={isSubmitting}>
                {isSubmitting
                  ? t("admin.saving")
                  : project
                  ? t("admin.update")
                  : t("admin.dashboardSubmit")}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFormModal;
